import VideoBackgroundProcessor from './video-background-processor';
import { EFFECT_MODE } from 'flow/constants/video-background-constants';

type EffectMode = (typeof EFFECT_MODE)[keyof typeof EFFECT_MODE];

const LOG_PREFIX = '[ProcessorManager]';

/**
 * VideoStreamProcessorManager
 *
 * Manages the lifecycle of video stream processing with background effects.
 * This is a singleton that coordinates between the raw camera stream and
 * the processed output stream.
 */
class VideoStreamProcessorManager {
    private processor: VideoBackgroundProcessor | null = null;
    private videoElement: HTMLVideoElement | null = null;
    private rawStream: MediaStream | null = null;
    private processedStream: MediaStream | null = null;
    private isProcessing: boolean = false;
    private _processingMutex: boolean = false;
    private _pendingCleanup: Promise<void> | null = null;
    private _pendingProcessing: Promise<MediaStream | null> | null = null;

    /**
     * Process a video stream with background effects
     * @param inputStream - Raw camera stream
     * @param blurAmount - Blur amount in pixels
     * @param mode - Effect mode: 'none', 'blur', 'image'
     * @param backgroundImageUrl - URL for background image (if mode is 'image')
     * @returns Processed or original stream
     */
    async processStream(
        inputStream: MediaStream | null,
        blurAmount?: number,
        mode: EffectMode = EFFECT_MODE.NONE,
        backgroundImageUrl: string | null = null,
    ): Promise<MediaStream | null> {
        if (!inputStream) {
            return null;
        }

        // Wait for any in-progress processing to complete before starting new processing
        // This prevents race conditions when lobby is quickly skipped and meeting starts
        if (this._processingMutex && this._pendingProcessing !== null) {
            console.info(
                `${LOG_PREFIX} Waiting for in-progress processing to complete`,
            );
            try {
                await this._pendingProcessing;
            } catch (error) {
                console.warn(
                    `${LOG_PREFIX} Previous processing failed, continuing with new request:`,
                    error,
                );
            }
        }

        if (this._processingMutex) {
            console.warn(
                `${LOG_PREFIX} Already processing, returning existing stream`,
            );
            return this.processedStream || inputStream;
        }

        this._processingMutex = true;
        this._pendingProcessing = this._doProcessStream(
            inputStream,
            blurAmount,
            mode,
            backgroundImageUrl,
        );

        try {
            return await this._pendingProcessing;
        } finally {
            this._pendingProcessing = null;
        }
    }

    /**
     * Internal implementation of stream processing
     * @private
     */
    private async _doProcessStream(
        inputStream: MediaStream,
        blurAmount?: number,
        mode: EffectMode = EFFECT_MODE.NONE,
        backgroundImageUrl: string | null = null,
    ): Promise<MediaStream | null> {
        try {
            if (mode === EFFECT_MODE.NONE) {
                // When disabling effects, clean up the processor but preserve the raw stream
                await this._cleanupProcessorOnly();
                return inputStream;
            }

            if (this._pendingCleanup !== null) {
                await this._pendingCleanup;
                this._pendingCleanup = null;
            }

            if (this.isProcessing) {
                await this.cleanup();
            }

            this.rawStream = inputStream;

            if (!this.videoElement) {
                this.videoElement = document.createElement('video');
                this.videoElement.autoplay = true;
                this.videoElement.muted = true;
                this.videoElement.playsInline = true;
            }

            this.videoElement.srcObject = inputStream;
            await this.videoElement.play();

            if (!this.processor) {
                this.processor = new VideoBackgroundProcessor();
            }

            if (mode === EFFECT_MODE.IMAGE && backgroundImageUrl) {
                await this.processor.setBackgroundImage(backgroundImageUrl);
                this.processor.setMode(EFFECT_MODE.IMAGE);
            } else if (mode === EFFECT_MODE.BLUR) {
                this.processor.setMode(EFFECT_MODE.BLUR);
                if (blurAmount !== undefined) {
                    this.processor.setBlurAmount(blurAmount);
                }
            } else {
                this.processor.setMode(EFFECT_MODE.NONE);
            }

            this.processedStream = await this.processor.startProcessing(
                this.videoElement,
            );
            this.isProcessing = true;

            return this.processedStream;
        } catch (error) {
            // Keep the raw camera tracks live so the caller can fall back to them.
            console.error(`${LOG_PREFIX} processing failed:`, error);
            await this._performProcessorCleanup();
            throw error;
        } finally {
            this._processingMutex = false;
        }
    }

    /**
     * Update effect settings on the fly
     * @param blurAmount - Blur amount in pixels
     * @param mode - Effect mode: 'none', 'blur', 'image'
     * @param backgroundImageUrl - URL for background image (if mode is 'image')
     */
    async updateEffectSettings(
        blurAmount?: number,
        mode: EffectMode = EFFECT_MODE.NONE,
        backgroundImageUrl: string | null = null,
    ): Promise<void> {
        if (!this.processor) {
            return;
        }

        if (mode === EFFECT_MODE.IMAGE && backgroundImageUrl) {
            await this.processor.setBackgroundImage(backgroundImageUrl);
            this.processor.setMode(EFFECT_MODE.IMAGE);
        } else if (mode === EFFECT_MODE.BLUR) {
            // Ensure we don't have a frame with IMAGE mode but no image
            if (blurAmount !== undefined) {
                this.processor.setBlurAmount(blurAmount);
            }
            await this.processor.setBackgroundImage(null);
            this.processor.setMode(EFFECT_MODE.BLUR);
        } else {
            this.processor.setMode(EFFECT_MODE.NONE);
            await this.processor.setBackgroundImage(null);
        }
    }

    /**
     * Update blur settings on the fly (legacy method for backwards compatibility)
     * @param enableBlur - Whether to enable blur
     * @param blurAmount - Blur amount in pixels
     */
    updateBlurSettings(enableBlur: boolean, blurAmount: number): Promise<void> {
        return this.updateEffectSettings(
            blurAmount,
            enableBlur ? EFFECT_MODE.BLUR : EFFECT_MODE.NONE,
            null,
        );
    }

    /**
     * Get the current processed stream (or null if not processing)
     * @returns The processed stream or null
     */
    getProcessedStream(): MediaStream | null {
        return this.processedStream;
    }

    /**
     * Get the raw input stream
     * @returns The raw input stream or null
     */
    getRawStream(): MediaStream | null {
        return this.rawStream;
    }

    /**
     * Check if currently processing
     * @returns Whether currently processing
     */
    isActive(): boolean {
        return this.isProcessing && (this.processor?.isActive() ?? false);
    }

    /**
     * Tear down the processor but leave the raw stream running — the caller still
     * owns it (used when disabling effects, and on the processing-error path).
     */
    private async _cleanupProcessorOnly(): Promise<void> {
        this.isProcessing = false;

        if (this.processor) {
            try {
                await this.processor.stopProcessing();
            } catch (error) {
                console.error(`${LOG_PREFIX} stopProcessing error:`, error);
            }
            this.processor = null;
        }

        if (this.videoElement) {
            this.videoElement.srcObject = null;
            this.videoElement = null;
        }

        this.processedStream = null;
    }

    /**
     * Clean up resources
     */
    async cleanup(): Promise<void> {
        if (this._pendingCleanup !== null) {
            return this._pendingCleanup;
        }

        this._pendingCleanup = this._performCleanup();
        try {
            return await this._pendingCleanup;
        } finally {
            this._pendingCleanup = null;
        }
    }

    /**
     * Error-path cleanup: tear down the processor and drop our reference to the
     * raw stream, but leave its tracks live for the caller's fallback.
     */
    private async _performProcessorCleanup(): Promise<void> {
        await this._cleanupProcessorOnly();
        this.rawStream = null;
    }

    /**
     * Full cleanup, including stopping the raw stream's tracks. Not for the
     * processing-error path: the raw stream is the caller's fallback there, so
     * stopping it would drop the camera (VID-125) — use _performProcessorCleanup.
     */
    private async _performCleanup(): Promise<void> {
        this.isProcessing = false;

        if (this.processor) {
            try {
                await this.processor.stopProcessing();
            } catch (error) {
                console.error(`${LOG_PREFIX} stopProcessing error:`, error);
            }
            this.processor = null;
        }

        if (this.rawStream) {
            this.rawStream.getTracks().forEach((track) => {
                try {
                    track.stop();
                } catch (error) {
                    console.error(
                        `${LOG_PREFIX} Failed to stop raw track:`,
                        error,
                    );
                }
            });
        }

        if (this.videoElement) {
            this.videoElement.srcObject = null;
            this.videoElement = null;
        }

        this.processedStream = null;
        this.rawStream = null;
    }
}

const processorManager = new VideoStreamProcessorManager();
export default processorManager;
